import { useState, useEffect, useCallback, useRef } from 'react';

const NOTIFICATION_STORAGE_KEY = 'bf_notifications_enabled';

export function useNotificationSound() {
  const [isEnabled, setIsEnabled] = useState(() => {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });
  
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    localStorage.setItem(NOTIFICATION_STORAGE_KEY, String(isEnabled));
  }, [isEnabled]);

  const playNotificationSound = useCallback(() => {
    if (!isEnabled) return;

    try {
      // Create AudioContext on demand (required for autoplay policies)
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      
      // Create a pleasant notification sound
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Pleasant bell-like tone
      oscillator.frequency.setValueAtTime(880, ctx.currentTime); // A5
      oscillator.frequency.setValueAtTime(1108.73, ctx.currentTime + 0.1); // C#6
      oscillator.frequency.setValueAtTime(1318.51, ctx.currentTime + 0.2); // E6
      
      oscillator.type = 'sine';
      
      // Volume envelope
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  }, [isEnabled]);

  const toggleNotifications = useCallback(() => {
    setIsEnabled(prev => !prev);
  }, []);

  return {
    isEnabled,
    toggleNotifications,
    playNotificationSound,
  };
}
